import React, { useContext } from 'react';
import { Footer, TripleAction, RouterContext } from '../../gs-component-right-rail';
import { FormikTouched } from 'formik';
import ComponentStateContext from '../ComponentStateContext';
import Content from '../Content';
import { ModalType, Person, Invitation, Household, Event, FormProps, FocusOn } from '../interfaces';
import { getPartyType } from '../helpers';
import { track } from '../helpers/analytics';
import LegalSms from '../Legal/LegalSms';

interface Values {
  people: Person[];
  rsvpNote: Household['rsvpNote'];
  invitations: Invitation[];
}

interface RightRailContentProps {
  values: Values;
  dirty: boolean;
  submitForm: () => void;
  isValid: boolean;
  household?: Household;
  events: Event[];
  type: ModalType;
  focusOn: FocusOn;
  selectedEvent?: Event;
  onClose: () => void;
  touched: FormikTouched<FormProps>;
  analyticsObject?: object;
  setShouldClose: (arg0: boolean) => void;
  displayFullAddress: boolean;
}

const RightRailContent: React.FC<RightRailContentProps> = ({
  household,
  events,
  selectedEvent,
  onClose,
  values,
  submitForm,
  isValid,
  dirty,
  type,
  focusOn,
  setShouldClose,
  displayFullAddress,
  touched = {},
  analyticsObject = {},
}) => {
  const { push } = useContext(RouterContext);
  const { guestsDeleted } = useContext(ComponentStateContext);

  let tAAddProps;
  if (focusOn === FocusOn.all) {
    tAAddProps = {
      primaryText: 'Save & add another guest',
      secondaryText: 'Save & close',
      tertiaryText: 'Cancel',
      primaryOnClick: (): void => {
        fireIndividualGuestAnalytics(values.people.length);
        submitForm();
      },
      secondaryOnClick: (): void => {
        fireIndividualGuestAnalytics(values.people.length);
        setShouldClose(true);
        submitForm();
      },
      tertiaryOnClick: (): void => {
        closeRail();
      },
    };
  } else {
    tAAddProps = {
      primaryText: 'Save & continue',
      secondaryText: 'Cancel',
      primaryOnClick: (): void => {
        fireIndividualGuestAnalytics(values.people.length);
        setShouldClose(true);
        submitForm();
      },
      secondaryOnClick: (): void => {
        closeRail();
      },
    };
  }

  const tAEditProps = {
    primaryText: focusOn === FocusOn.all ? 'Save' : 'Save & continue',
    secondaryText: 'Cancel',
    tertiaryText: focusOn === FocusOn.all ? 'Delete party' : undefined,
  };

  const fireIndividualGuestAnalytics = (numOfPeople: number): void => {
    let source;
    let action;

    if (type === ModalType.add) {
      const typeOfParty = getPartyType(numOfPeople);
      source = `add guest modal - ${typeOfParty}`;
      action = 'Guest Added';
    } else {
      source = 'edit modal';
      action = 'Guest Updated';
    }

    for (let i = 0; i < numOfPeople; i += 1) {
      track(action, { source, ...analyticsObject });
    }
  };

  const callEditAnalytics = (numOfPeople: number): void => {
    const touchedFields = Object.entries(touched);

    if (touchedFields.find(entry => entry[0] !== 'people')) {
      fireIndividualGuestAnalytics(numOfPeople);
    } else if (touched.people) {
      fireIndividualGuestAnalytics(touched.people.length);
    }

    for (let i = 0; i < guestsDeleted; i += 1) {
      track('Guest Deleted', {
        source: 'edit modal',
        ...analyticsObject,
      });
    }

    if (household && household.people) {
      const guestsAdded = values.people.length - household.people.length;
      for (let i = 0; i < guestsAdded; i += 1) {
        track('Guest Added', {
          source: 'edit modal',
          ...analyticsObject,
        });
      }
    }
  };

  const closeRail = (): void => {
    if (dirty) {
      push('cancel');
    } else {
      onClose();
    }
  };

  return (
    <>
      <Content
        type={type}
        events={events}
        selectedEvent={selectedEvent}
        people={values.people}
        rsvpNote={values.rsvpNote || ''}
        invitations={values.invitations}
        displayFullAddress={displayFullAddress}
        focusOn={focusOn}
      />
      {focusOn && focusOn === FocusOn.phone && <LegalSms />}
      <Footer>
        {type === ModalType.add ? (
          <TripleAction primaryDisabled={!isValid} secondaryDisabled={!isValid} {...tAAddProps} />
        ) : (
          <TripleAction
            primaryOnClick={(): void => {
              callEditAnalytics(values.people.length);
              setShouldClose(true);
              submitForm();
              track('Guest Modal Interaction', analyticsObject);
            }}
            secondaryOnClick={closeRail}
            tertiaryOnClick={(): void => {
              track('Guest Delete Initiated', analyticsObject);
              push('delete');
            }}
            primaryDisabled={!isValid}
            {...tAEditProps}
          />
        )}
      </Footer>
    </>
  );
};

export default RightRailContent;
